// 261010 Epic 2 / P2b-1 + P2b-2 + P2b-3 — phần dùng chung của các bước có thao tác ở khung mới.
import React from 'react';
import { Alert, Button, Space } from 'antd';
import type { FilmFailedJob, FilmRunningJob } from '../../types/film';
import type { WorkflowEventStatus, WorkflowStepKey, WorkflowStepState } from '../../types/filmWorkflow';
import type { ActingFlag, ActingOutcome } from '../../types/filmActing';
import type { PromptPick } from '../../types/filmStepReview';
import type { ActionError } from './workflowActionPolicy';
import type { StepReviewsController } from './useStepReviews';
import type { AssemblyClipWarning } from './clipReviewPolicy';

/** Mọi bước có thao tác nhận cùng bộ prop này từ `FilmWorkflowPage`. */
export interface InteractiveProps {
  planId: string;
  storyId: string;
  /** Trạng thái của video ở bước đang xem (theo `workflow-status`). */
  stepState: WorkflowStepState;
  /** Việc nền đang chạy / vừa lỗi — nguồn `workflow-status` (tải lại trang vẫn thấy). */
  jobs: FilmRunningJob[];
  failures: FilmFailedJob[];
  /** Sau MỌI thao tác ghi (thành công hay lỗi): trang tải lại CẢ dữ liệu chi tiết của widget LẪN `workflow-status`. Không suy trạng thái từ tên thao tác. */
  onActionDone: () => void;
  /** Hỏi `workflow-status` NGAY (sau 202) và trả jobs/failures mới. */
  onJobsRefresh: () => Promise<{ jobs: FilmRunningJob[]; failures?: FilmFailedJob[] } | undefined>;
  /** 261010 P8b-2 — bước Ghép: `status.events` nguyên (Rev 2 #6) để vẽ timeline; assemblyCtxOf chỉ đọc eventId/eventOrder. */
  events?: WorkflowEventStatus[];
  onOpenEvent?: (eventId: string) => void;
  /** 261010 P2b-3 — bước Kịch bản / Đạo diễn: báo trang "đang có nháp chưa lưu" để trang hỏi "Bỏ thay đổi chưa lưu?" trước khi đổi bước / video / quay về bảng. */
  setEditDirty?: (dirty: boolean) => void;
  /** 261010 P2b-3 — liên kết từ dòng "đã cũ" sang bước 7 (Prompt) / bước 8 (Video) của CHÍNH video này. */
  onGoStep?: (step: WorkflowStepKey) => void;
  /** 261010 P3e — cờ lỗi mềm Diễn xuất (`ACTING_FAILED` / `ACTING_MANUAL_EDIT_EXISTS`) của ĐÚNG video này, trang tính từ `workflow-status.alerts`. Vắng ⇒ không cờ. */
  actingFlags?: ActingFlag[];
  /** 261010 P3e (BE lượt 2) — `workflow-status.workflowRuns.stage2.actingOutcomes`: kết quả pass Diễn xuất của lượt chặng 2 gần nhất, theo video. Vắng ⇒ không hiện. */
  actingOutcomes?: ActingOutcome[];
  /** 261010 P5d — bước Prompt: câu AI (phiếu "AI chấm prompt") trích + các bản chữ lưu trong phiếu, mà người xem vừa bấm "Xem câu này trong prompt"; `null` / vắng ⇒ không đánh dấu. Thẻ phiếu do TRANG dựng, bước chỉ vẽ khung đánh dấu (chỉ đọc). */
  promptPick?: PromptPick | null;
  onClosePromptPick?: () => void;
  /** 261010 P6 — bước Video: chủ dữ liệu phiếu "AI chấm clip" của video (do TRANG sở hữu, chia cho thẻ phiếu trên đầu và các dòng bản clip) + bản đang xem phiếu. Vắng ⇒ bước Video chạy y như trước P6. */
  clipReviews?: ClipReviewsBinding;
  /** 261010 P6 — bước Ghép: cảnh báo CHỮ về phiếu AI chấm clip của các bản đang chọn (trang tính từ `workflow-status` + chi tiết plan). Không chặn nút / luồng ghép. */
  assemblyClipWarnings?: AssemblyClipWarning[];
}

export interface ClipReviewsBinding {
  ctl: StepReviewsController;
  /** Bản clip đang xem ở thẻ phiếu trên đầu (null = chưa có bản DONE). */
  focusClipId: string | null;
  onFocusClip: (clipId: string) => void;
}

/** Lỗi nghiệp vụ của thao tác — câu tiếng Việt + nút Tải lại, ngay trong khối bước (không chỉ toast). */
export const StepErrorAlert: React.FC<{ error: ActionError | null; onReload: () => void; onClose: () => void; onRetry?: () => void }> = ({ error, onReload, onClose, onRetry }) => {
  if (!error) return null;
  return (
    <Alert type={['BUSY', 'CHANGED', 'CHANGED_ELSEWHERE', 'EDITED', 'PROMPT', 'ENGINE', 'CLIP_CONFIG'].includes(error.kind) ? 'warning' : 'error'} showIcon closable onClose={onClose}
      style={{ marginBottom: 10 }} data-testid="wf-action-error" message={error.text}
      action={<Space size={4}>{error.retry && onRetry ? <Button size="small" type="primary" data-testid="wf-action-retry" onClick={() => { onRetry(); }}>Thử lại</Button> : null}<Button size="small" onClick={() => { onReload(); onClose(); }}>Tải lại</Button></Space>} />
  );
};

export const RunningTag: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="wf-chip doing" role="status" data-testid="wf-running">{children}</span>
);
